import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  getFinPlanWithFact,
  getFinPlanSectionBlocks,
} from "../../../../api/projectEstimatesApi";
import "./PlanFactAnalysis.css";

const SECTION_GROUPS = [
  { id: "working_design", label: "РАБОЧЕЕ ПРОЕКТИРОВАНИЕ", key: "construction_design" },
  { id: "bim", label: "ВИМ КООРДИНИРОВАНИЕ", key: "bim_coordination" },
  { id: "quality", label: "КОНТРОЛЬ КАЧЕСТВА", key: "quality_control" },
  { id: "delivery", label: "ВЕДЕНИЕ ПРОЕКТА", key: "project_delivery" },
  { id: "management", label: "УПРАВЛЕНИЕ ПРОЕКТАМИ", key: "project_management" },
];

const formatMoney = (v) =>
  v != null && !Number.isNaN(Number(v))
    ? Number(v).toLocaleString("ru-RU", { maximumFractionDigits: 0 })
    : "0";

const formatDays = (v) =>
  v != null && !Number.isNaN(Number(v)) ? Number(v).toFixed(1) : "0";

const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const getPerformerName = (performer) =>
  performer?.name ??
  performer?.employee?.name ??
  performer?.employee_name ??
  "Специалист";

const PlanFactAnalysis = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set(SECTION_GROUPS.map((g) => g.id)));
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [sectionBlocks, setSectionBlocks] = useState({});
  const [sectionBlocksLoading, setSectionBlocksLoading] = useState({});

  const contractEstimateTotal = useMemo(() => {
    return planData?.contract_estimate_total ?? 0;
  }, [planData]);

  const factTotal = useMemo(() => {
    return planData?.fact_total_cost ?? 0;
  }, [planData]);

  const deviationTotal = contractEstimateTotal - factTotal;

  const buildSectionsTree = useMemo(() => {
    if (!planData) return [];

    const cd = planData.construction_design || [];
    const cdProject = planData.construction_design_project || [];
    const allConstructionDesign = [...cd, ...cdProject];

    return SECTION_GROUPS.map((gr) => {
      let sections = [];
      if (gr.key === "construction_design") {
        sections = allConstructionDesign.map((s) => {
          const sectionId = s.section?.id;
          const planEstimate = s.price_with_payroll_taxes ?? s.money_expenditures?.planned ?? 0;
          const planDays = s.day_efforts?.total ?? 0;
          return {
            id: `wd-${sectionId}`,
            sectionId,
            name: s.section?.name ?? "",
            planEstimate,
            planDays,
            factCost: s.fact_total_cost ?? 0,
            factDays: s.fact_total_days ?? 0,
            factHours: s.fact_total_hours ?? 0,
            isWorkingDesign: true,
          };
        });
      } else {
        const groupData = planData[gr.key];
        const roles = groupData?.roles || [];
        sections = roles.map((r, idx) => ({
          id: `${gr.id}-${r.section_id ?? idx}`,
          sectionId: r.section_id,
          name: r.role ?? r.name ?? "",
          planEstimate: r.money_expenditures?.direct ?? r.total ?? 0,
          planDays: r.day_efforts?.direct ?? 0,
          factCost: r.fact_total_cost ?? 0,
          factDays: r.fact_total_days ?? 0,
          factHours: r.fact_total_hours ?? 0,
          isWorkingDesign: false,
        }));
      }
      return { ...gr, sections };
    });
  }, [planData]);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFinPlanWithFact(projectId)
      .then((data) => {
        if (!cancelled) {
          setPlanData(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "Не удалось загрузить данные");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId]);

  const loadSectionBlocks = useCallback(async (sectionId) => {
    if (!projectId || sectionId == null) return;
    const key = String(sectionId);
    if (sectionBlocks[key] || sectionBlocksLoading[key]) return;

    setSectionBlocksLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const data = await getFinPlanSectionBlocks(projectId, sectionId);
      setSectionBlocks((prev) => ({ ...prev, [key]: data }));
    } catch (err) {
      console.error("Error loading section blocks:", err);
    } finally {
      setSectionBlocksLoading((prev) => ({ ...prev, [key]: false }));
    }
  }, [projectId, sectionBlocks, sectionBlocksLoading]);

  const toggleGroup = (id) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSection = (sec) => {
    const rowId = sec.id;
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
        if (sec.isWorkingDesign && sec.sectionId != null) {
          loadSectionBlocks(sec.sectionId);
        }
      }
      return next;
    });
  };

  if (!projectId) {
    return (
      <div className="plan-fact-page">
        <div className="plan-fact-empty">Выберите проект</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="plan-fact-page">
        <div className="plan-fact-loading">Загрузка...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="plan-fact-page">
        <div className="plan-fact-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="plan-fact-page">
      <h2 className="plan-fact-title">План-фактный анализ</h2>

      <div className="plan-fact-cards">
        <div className="plan-fact-card">
          <span className="plan-fact-card-label">Смета по договору</span>
          <span className="plan-fact-card-icon">📋</span>
          <span className="plan-fact-card-value">{formatMoney(contractEstimateTotal)}</span>
        </div>
        <div className="plan-fact-card">
          <span className="plan-fact-card-label">Факт</span>
          <span className="plan-fact-card-icon">📊</span>
          <span className="plan-fact-card-value plan-fact-card-value--fact">
            ~ {formatMoney(factTotal)}
          </span>
        </div>
        <div className="plan-fact-card">
          <span className="plan-fact-card-label">Отклонение</span>
          <span className="plan-fact-card-icon">📈</span>
          <span
            className={`plan-fact-card-value plan-fact-card-value--deviation ${
              deviationTotal >= 0 ? "positive" : "negative"
            }`}
          >
            ~ {deviationTotal >= 0 ? "" : "-"}{formatMoney(Math.abs(deviationTotal))}
          </span>
        </div>
      </div>

      <div className="plan-fact-table-wrap">
        <table className="plan-fact-table">
          <thead>
            <tr>
              <th className="plan-fact-th-section">Раздел</th>
              <th className="plan-fact-th-num">Смета по договору</th>
              <th className="plan-fact-th-num">Факт</th>
              <th className="plan-fact-th-num">Отклонение от сметы</th>
            </tr>
          </thead>
          <tbody>
            {buildSectionsTree.map((group) => (
              <React.Fragment key={group.id}>
                <tr
                  className="plan-fact-group-row"
                  onClick={() => toggleGroup(group.id)}
                >
                  <td className="plan-fact-td-section">
                    <span
                      className={`plan-fact-expand-icon ${
                        expandedGroups.has(group.id) ? "expanded" : ""
                      }`}
                    >
                      ▶
                    </span>
                    {group.label}
                  </td>
                  <td className="plan-fact-td-num" />
                  <td className="plan-fact-td-num" />
                  <td className="plan-fact-td-num" />
                </tr>
                {expandedGroups.has(group.id) &&
                  (group.sections || []).map((sec) => {
                    const plan = Number(sec.planEstimate) || 0;
                    const fact = Number(sec.factCost) || 0;
                    const dev = plan - fact;
                    const rowId = sec.id;
                    const isSectionExpanded = expandedSections.has(rowId);
                    const sectionKey = String(sec.sectionId);
                    const blocksData = sectionBlocks[sectionKey];
                    const isBlocksLoading = sectionBlocksLoading[sectionKey];

                    return (
                      <React.Fragment key={rowId}>
                        <tr
                          className="plan-fact-section-row"
                          onClick={() => toggleSection(sec)}
                        >
                          <td className="plan-fact-td-section">
                            {sec.isWorkingDesign && (
                              <span
                                className={`plan-fact-expand-icon subsection ${
                                  isSectionExpanded ? "expanded" : ""
                                }`}
                              >
                                ▶
                              </span>
                            )}
                            {sec.name}
                          </td>
                          <td className="plan-fact-td-num">
                            {formatMoney(plan)}
                          </td>
                          <td className="plan-fact-td-num plan-fact-fact-cell">
                            ~ {formatMoney(fact)}
                          </td>
                          <td
                            className={`plan-fact-td-num plan-fact-deviation ${
                              dev >= 0 ? "positive" : "negative"
                            }`}
                          >
                            {dev >= 0 ? "+" : "-"} {formatMoney(Math.abs(dev))}
                          </td>
                        </tr>
                        {isSectionExpanded && sec.isWorkingDesign && (
                          <tr className="plan-fact-detail-row">
                            <td colSpan={4} className="plan-fact-detail-cell">
                              {isBlocksLoading ? (
                                <div className="plan-fact-blocks-loading">Загрузка блоков...</div>
                              ) : blocksData?.blocks?.length > 0 ? (
                                <div className="plan-fact-performers">
                                  <table className="plan-fact-performers-table">
                                    <thead>
                                      <tr>
                                        <th></th>
                                        <th>Исполнитель</th>
                                        <th>Трудозатраты</th>
                                        <th>Прямые затраты</th>
                                        <th>Трудозатраты</th>
                                        <th>Прямые затраты</th>
                                        <th>Трудозатраты</th>
                                        <th>Прямые затраты</th>
                                      </tr>
                                      <tr className="plan-fact-performers-subheader">
                                        <th></th>
                                        <th></th>
                                        <th colSpan={2} className="plan-fact-subheader-plan">План</th>
                                        <th colSpan={2} className="plan-fact-subheader-fact">Факт</th>
                                        <th colSpan={2} className="plan-fact-subheader-dev">Отклонение</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {blocksData.blocks.map((blk) => {
                                        const blockId = blk.id ?? blk.block?.id;
                                        const blockName = blk.name ?? blk.block?.name ?? "Блок";
                                        const blockPlanDays = toNumber(
                                          blk.estimate_days ?? blk.costs_per_block?.day_efforts,
                                        );
                                        const blockPlanCost = toNumber(
                                          blk.estimate_direct_cost ??
                                            blk.costs_per_block?.money_expenditures,
                                        );
                                        const blockFactDays = toNumber(
                                          blk.fact_total_days ??
                                            blk.fact_days ??
                                            (blk.fact_total_hours != null
                                              ? Number(blk.fact_total_hours) / 8
                                              : null),
                                        );
                                        const blockFactCost = toNumber(
                                          blk.fact_total_cost ?? blk.fact_cost,
                                        );
                                        const blockDevDays = blockPlanDays - blockFactDays;
                                        const blockDevCost = blockPlanCost - blockFactCost;
                                        const performers =
                                          blk.performers || blk.block?.performers || [];

                                        return (
                                          <React.Fragment key={blockId ?? blockName}>
                                            <tr className="plan-fact-block-header">
                                              <td colSpan={8}>{blockName}</td>
                                            </tr>
                                            <tr className="plan-fact-block-total">
                                              <td></td>
                                              <td>Все исполнители</td>
                                              <td>= {formatDays(blockPlanDays)}</td>
                                              <td>~ {formatMoney(blockPlanCost)}</td>
                                              <td>= {formatDays(blockFactDays)}</td>
                                              <td>~ {formatMoney(blockFactCost)}</td>
                                              <td className={blockDevDays >= 0 ? "positive" : "negative"}>
                                                = {formatDays(blockDevDays)}
                                              </td>
                                              <td className={blockDevCost >= 0 ? "positive" : "negative"}>
                                                ~ {blockDevCost >= 0 ? "" : "-"}{formatMoney(Math.abs(blockDevCost))}
                                              </td>
                                            </tr>
                                            {performers.map((p, pi) => (
                                              <tr key={`${blockId ?? blockName}-${pi}`} className="plan-fact-performer-row">
                                                <td></td>
                                                <td className="plan-fact-performer-name">
                                                  <span className="plan-fact-performer-bullet">○</span>
                                                  {getPerformerName(p)}
                                                  {p.work_percent != null && (
                                                    <span className="plan-fact-performer-share">
                                                      доля {p.work_percent}%
                                                    </span>
                                                  )}
                                                </td>
                                                <td>
                                                  ={" "}
                                                  {formatDays(
                                                    (blockPlanDays * (p.work_percent || 100)) /
                                                      100,
                                                  )}
                                                </td>
                                                <td>
                                                  ~{" "}
                                                  {formatMoney(
                                                    (blockPlanCost * (p.work_percent || 100)) /
                                                      100,
                                                  )}
                                                </td>
                                                {(() => {
                                                  const factDays =
                                                    p.fact_days != null
                                                      ? p.fact_days
                                                      : p.fact_hours != null
                                                        ? p.fact_hours / 8
                                                        : null;
                                                  const factCost =
                                                    p.fact_cost != null ? p.fact_cost : null;

                                                  if (factDays == null && factCost == null) {
                                                    return (
                                                      <>
                                                        <td>–</td>
                                                        <td>–</td>
                                                        <td>–</td>
                                                        <td>–</td>
                                                      </>
                                                    );
                                                  }

                                                  const planDaysShare =
                                                    (blockPlanDays * (p.work_percent || 100)) /
                                                    100;
                                                  const planCostShare =
                                                    (blockPlanCost * (p.work_percent || 100)) /
                                                    100;

                                                  const devDays =
                                                    factDays != null
                                                      ? planDaysShare - factDays
                                                      : null;
                                                  const devCost =
                                                    factCost != null
                                                      ? planCostShare - factCost
                                                      : null;

                                                  return (
                                                    <>
                                                      <td>
                                                        {factDays != null
                                                          ? `= ${formatDays(factDays)}`
                                                          : "–"}
                                                      </td>
                                                      <td>
                                                        {factCost != null
                                                          ? `~ ${formatMoney(factCost)}`
                                                          : "–"}
                                                      </td>
                                                      <td
                                                        className={
                                                          devDays != null
                                                            ? devDays >= 0
                                                              ? "positive"
                                                              : "negative"
                                                            : ""
                                                        }
                                                      >
                                                        {devDays != null
                                                          ? `= ${formatDays(devDays)}`
                                                          : "–"}
                                                      </td>
                                                      <td
                                                        className={
                                                          devCost != null
                                                            ? devCost >= 0
                                                              ? "positive"
                                                              : "negative"
                                                            : ""
                                                        }
                                                      >
                                                        {devCost != null
                                                          ? `~ ${
                                                              devCost >= 0 ? "" : "-"
                                                            }${formatMoney(Math.abs(devCost))}`
                                                          : "–"}
                                                      </td>
                                                    </>
                                                  );
                                                })()}
                                              </tr>
                                            ))}
                                          </React.Fragment>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="plan-fact-no-blocks">Нет данных по блокам</div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="plan-fact-total-row">
              <td className="plan-fact-td-section">ВСЕ РАЗДЕЛЫ</td>
              <td className="plan-fact-td-num">
                {formatMoney(contractEstimateTotal)}
              </td>
              <td className="plan-fact-td-num">{formatMoney(factTotal)}</td>
              <td
                className={`plan-fact-td-num plan-fact-deviation ${
                  deviationTotal >= 0 ? "positive" : "negative"
                }`}
              >
                {formatMoney(deviationTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="plan-fact-actions">
        <button type="button" className="plan-fact-btn plan-fact-btn-pdf">
          Печать PDF
        </button>
      </div>
    </div>
  );
};

export default PlanFactAnalysis;
